import PropTypes from 'prop-types';
import React from 'react';
import Browser from '@wetransfer/concorde-browser';

// We only want to record the scrollbar width once,
// technically it's possible that it changes while using
// but it's highly unlikely and much more complicated.
let cachedWidth = false;

export default class Scrollbar extends React.Component {
  static propTypes = {
    onScrubStart: PropTypes.func,
    onScrubEnd: PropTypes.func,
    onScrub: PropTypes.func,
    visible: PropTypes.bool,
    parent: PropTypes.object,
    height: PropTypes.number,
    top: PropTypes.number,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      mouseY: 0,
      thumbState: {},
      isDragging: false,
    };

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
  }

  // Return the width of the scrollbar this browser shows
  static calculateWidth() {
    // If we already calculated it once, do it again.
    if (cachedWidth) {
      return cachedWidth;
    }

    // So to calculate this, we basically temporarily insert
    // a secret div with overflow enabled and see what the
    // difference is between offsetWidth and clientWidth to
    // determine how big the scrollbar most likely is.
    const node = document.createElement('div');
    node.className = 'scrollable__width-test';
    document.body.appendChild(node);
    cachedWidth = node.offsetWidth - node.clientWidth;

    // Then we remove it, BAM.
    document.body.removeChild(node);
    return cachedWidth;
  }

  // When the mouse goes down on the scrubber, let's start the show.
  onMouseDown(e) {
    if (this.state.isDragging) {
      return;
    }
    this.setState({
      mouseY: e.pageY,
      isDragging: true,
      thumbState: this.props.parent.getScrollState(),
    });
    this.props.onScrubStart();
    this.appendBlockingNode();
    e.preventDefault();
    return false;
  }

  onMouseMove(e) {
    if (!this.state.isDragging) {
      return;
    }
    const delta = e.pageY - this.state.mouseY;
    this.props.onScrub(this.state.thumbState, delta);
  }

  onMouseUp() {
    if (!this.state.isDragging) {
      return;
    }
    this.props.onScrubEnd();
    this.removeBlockingNode();
    this.setState({
      isDragging: false,
    });
  }

  componentWillUnmount() {
    this.removeBlockingNode();
  }

  appendBlockingNode() {
    if (this.blockingNode) {
      return;
    }

    this.blockingNode = document.createElement('div');
    this.blockingNode.className = 'scrollable__block';
    this.blockingNode.addEventListener('mouseup', this.onMouseUp, false);
    window.addEventListener('mouseup', this.onMouseUp, false);
    window.addEventListener('mousemove', this.onMouseMove, false);
    document.querySelector('body').appendChild(this.blockingNode);
  }

  removeBlockingNode() {
    if (!this.blockingNode) {
      return;
    }
    window.removeEventListener('mouseup', this.onMouseUp, false);
    window.removeEventListener('mousemove', this.onMouseMove, false);
    try {
      document.body.removeChild(this.blockingNode);
    } catch (e) {
      /* it's no problem */
    }

    this.blockingNode = null;
  }

  render() {
    let className = 'scrollable__scrollbar';
    if (this.props.visible && !Browser.isMobile) {
      className += ' scrollable__scrollbar--visible';
    }
    if (isNaN(this.props.top)) {
      return null;
    }
    if (isNaN(this.props.height)) {
      return null;
    }

    // Set the scrollbar thumb in the right position
    const thumbStyle = {
      height: this.props.height + 'px',
      transform: 'translateY(' + Math.max(0, this.props.top) + 'px)',
      MozTransform: 'translateY(' + Math.max(0, this.props.top) + 'px)',
      WebkitTransform: 'translateY(' + Math.max(0, this.props.top) + 'px)',
    };

    // Are we dragging right now?
    if (this.state.isDragging) {
      className += ' scrollable__scrollbar-thumb--grabbed';
    }

    return (
      <div ref={(node) => (this.node = node)} className={className}>
        <div
          onMouseDown={this.onMouseDown}
          className="scrollable__scrollbar-thumb"
          style={thumbStyle}
        />
      </div>
    );
  }
}
